import { useId, useMemo, useRef, useState, useEffect } from "react";

export type ChairMemberOption = { id: number; label: string; group?: string };

function matchesQuery(o: ChairMemberOption, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  return o.label.toLowerCase().includes(s);
}

interface Props {
  options: ChairMemberOption[];
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Aanroepen wanneer de voorzitter op “Vraag om toevoegen” drukt (niet bij kiezen in het veld). */
  onRequestAdd: (contactId: number) => void | Promise<void>;
  busyContactId?: number | null;
}

/**
 * Zoekveld voor voorzitters: alleen namen in de lijst, per regel een knop om een goedkeuringsverzoek te sturen.
 */
export default function ChairMemberAddSearch({
  options,
  placeholder = "Typ een naam…",
  emptyMessage = "Geen resultaten",
  disabled,
  onRequestAdd,
  busyContactId,
}: Props) {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(
    () => (open ? options.filter((o) => matchesQuery(o, query)) : []),
    [open, options, query]
  );

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={rootRef} className="searchable-select chair-member-add-search" style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        ref={inputRef}
        type="text"
        className="searchable-select-input search-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        placeholder={placeholder}
        disabled={disabled}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="searchable-select-list"
          style={{
            position: "absolute",
            zIndex: 50,
            left: 0,
            right: 0,
            top: "100%",
            marginTop: 4,
            maxHeight: 280,
            overflowY: "auto",
            background: "var(--surface, #fff)",
            border: "1px solid var(--border, #ccc)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            listStyle: "none",
            padding: "0.25rem 0",
          }}
        >
          {filtered.length === 0 ? (
            <li className="searchable-select-empty" style={{ padding: "0.5rem 0.75rem", fontSize: "0.9rem" }}>
              {emptyMessage}
            </li>
          ) : (
            filtered.map((o) => (
              <li
                key={o.id}
                role="option"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "0.35rem 0.5rem",
                  borderBottom: "1px solid var(--border-muted, #eee)",
                }}
              >
                <span style={{ minWidth: 0 }}>{o.label}</span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={busyContactId === o.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={async () => {
                    await onRequestAdd(o.id);
                  }}
                >
                  {busyContactId === o.id ? "Bezig…" : "Vraag om toevoegen"}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
